import {Device} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import {
  CouponInfoSheet,
  Coupon_BrandAndCategory,
  Coupon_DiscountValue,
  Coupon_HighTierCoupon_ProductsInformation,
  Coupon_HotDealCoupon_ProductAndBrandInformation,
  Coupon_SimpleCoupon_CouponTheme,
  Coupon_Tier,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  UnstyledButton,
  useMatchesMediaQuery,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {
  CouponAppOnlyBadge,
  FloatingBadge,
} from '../Coupons/CouponBadges/CouponBadges';
import {
  CouponExpiryDateCountdown,
  StyledCouponExpiryDateCountdownUnitComponent,
} from '../Coupons/CouponExpiryDateCountdown/CouponExpiryDateCountdown';
import {useCouponInfoContentPopperStateManager} from '../Coupons/CouponInfoContentPopper/useCouponInfoContentPopperStateManager';
import {hasAppOnlyRedeemableDevices} from '../Coupons/utils/hasAppOnlyRedeemableDevices';
import {useAppOnlyInfoFlyoutStateManager} from './components/AppOnlyInfoFlyout';
import {CouponFooter} from './components/CouponFooter';
import {
  BannerText,
  CouponHeader,
  StyledCouponTiers,
} from './components/CouponHeader';
import {TierShape} from './components/CouponTiers';
import {HotDealBadge} from './components/HotDealBadge';
import {CARD_ANIMATION_TIME} from './components/constants';
import {getIsExpiryDateLessThan} from './helpers/getIsExpiryDateLessThan';

const HOT_DEALS_COUPON_GRADIENT = `linear-gradient(241deg, #E52317 15.35%, #E5177A 48.49%, ${COLORS.PrimaryCoupon} 84.49%)`;

const defaultCouponTheme: {
  active: Coupon_SimpleCoupon_CouponTheme;
  inactive: Coupon_SimpleCoupon_CouponTheme;
  hotDeal: Coupon_SimpleCoupon_CouponTheme;
} = {
  active: {
    foregroundColor: COLORS.White,
    onBackgroundColor: COLORS.PrimaryCoupon,
    backgroundColor: COLORS.PrimaryCoupon,
  },
  inactive: {
    foregroundColor: COLORS.PrimaryCoupon,
    onBackgroundColor: COLORS.PrimaryCoupon,
    backgroundColor: COLORS.White,
  },
  hotDeal: {
    foregroundColor: COLORS.PrimaryCoupon,
    onBackgroundColor: HOT_DEALS_COUPON_GRADIENT,
    backgroundColor: COLORS.White,
  },
};

interface BaseProps {
  couponPromotionId: string;
  headline: string;
  expiryDate?: Date;
  theme?: Coupon_SimpleCoupon_CouponTheme;
  selectedTheme?: Coupon_SimpleCoupon_CouponTheme;
  info?: CouponInfoSheet;
  discountValue?: Coupon_DiscountValue;
  brandAndCategory?: Coupon_BrandAndCategory;
  redeemableDevices: Device[];
  label?: string;
  productAndBrandInformation?: Coupon_HotDealCoupon_ProductAndBrandInformation;
  hideCountdown?: boolean;
  tiers?: Coupon_Tier[];
  bannerText?: string;
  productsInformation?: Coupon_HighTierCoupon_ProductsInformation;
}

interface CouponCardBaseProps extends BaseProps {
  isHotDeal?: boolean;
  ['data-testid']?: string;
  isActive?: boolean;
  activateCouponCallback: VoidFunction;
  className?: string;
  onInfoIconClick?: (couponInfo?: CouponInfoSheet) => void;
  stateManagerCustomKey?: string;
  isDisplayMode?: boolean;
  isDisabled?: boolean;
  isInWallet?: boolean;
  setSelectedProduct?: (product: Product | undefined) => void;
  activateCouponOnAddToBasket: VoidFunction;
  disableUrgencyTriggers?: boolean;
}

// eslint-disable-next-line complexity
export const CouponCardBase: React.FC<CouponCardBaseProps> = ({
  headline,
  expiryDate,
  theme,
  selectedTheme,
  info,
  discountValue,
  brandAndCategory,
  label,
  productAndBrandInformation,
  className,
  isHotDeal,
  'data-testid': dataTestId,
  isActive,
  activateCouponCallback,
  activateCouponOnAddToBasket,
  redeemableDevices,
  hideCountdown,
  onInfoIconClick,
  tiers,
  bannerText,
  stateManagerCustomKey,
  isDisplayMode,
  isDisabled,
  isInWallet,
  setSelectedProduct,
  productsInformation,
  disableUrgencyTriggers,
}) => {
  const supportsHover = useMatchesMediaQuery('(pointer: fine)');
  const {open: openCouponInfoContentPopper} =
    useCouponInfoContentPopperStateManager(stateManagerCustomKey);
  const {open: openAppOnlyInfoFlyout} = useAppOnlyInfoFlyoutStateManager();
  const trackFeature = useTrackFeature();
  const {t} = useTranslation();

  const isOnlyAppCoupon =
    redeemableDevices && hasAppOnlyRedeemableDevices(redeemableDevices);

  const couponTheme = getCouponTheme({
    theme,
    selectedTheme,
    isHotDeal,
    isActive,
  });

  const handleInfoIconClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    trackFeature({
      name: 'coupon_card_info',
      eventType: 'click',
    });
    onInfoIconClick?.(info);
    openCouponInfoContentPopper();
  };

  const onCouponClick = () => {
    if (isOnlyAppCoupon) {
      if (!supportsHover) {
        openAppOnlyInfoFlyout();
      }
    } else {
      activateCouponCallback();
    }
  };

  const isExpiryDateLessThan12Hours = getIsExpiryDateLessThan({
    expiryDate,
    unit: 'hours',
    threshold: 12,
  });

  const isUrgencyCard = Boolean(
    expiryDate &&
      isExpiryDateLessThan12Hours &&
      isInWallet &&
      !isOnlyAppCoupon &&
      !isHotDeal &&
      !disableUrgencyTriggers,
  );

  return (
    <Wrapper
      $isHotDeal={isHotDeal && !disableUrgencyTriggers}
      data-is-active={isActive}
      data-testid={`${dataTestId}-${isOnlyAppCoupon ? 'app' : 'web'}`}
      $isDisabled={isDisabled}
      className={className}
      $isDisplayMode={isDisplayMode}
    >
      <Content
        $isOnlyAppCoupon={isOnlyAppCoupon}
        $isDisabled={isDisabled}
        $isHotDeal={isHotDeal && !disableUrgencyTriggers}
        $isDisplayMode={isDisplayMode}
      >
        {info && onInfoIconClick && (
          <InfoIcon
            data-testid={`${dataTestId}InfoIcon`}
            onClick={handleInfoIconClick}
            aria-label={t('Open coupon info window')}
          >
            <InfoSVG
              asset={GenericIconName.InfoWithoutFill}
              size="XS"
              fill={COLORS.BackgroundCoupon}
            />
          </InfoIcon>
        )}
        <InnerContent
          onClick={onCouponClick}
          data-testid={`${dataTestId}Content`}
          tabIndex={0}
        >
          <CouponHeader
            headline={headline}
            data-testid={dataTestId}
            discountValue={discountValue}
            tiers={tiers}
            couponTheme={couponTheme}
            isOnlyAppCoupon={isOnlyAppCoupon}
            supportsHover={supportsHover}
            bannerText={bannerText}
            isDisplayMode={isDisplayMode}
            isDisabled={isDisabled}
            isHotDeal={isHotDeal}
          />
          <StyledCouponFooter
            brandAndCategory={brandAndCategory}
            productAndBrandInformation={productAndBrandInformation}
            isOnlyAppCoupon={isOnlyAppCoupon}
            supportsHover={supportsHover && isOnlyAppCoupon}
            data-testid={dataTestId}
            $removeBorderTop={Boolean(bannerText)}
            isDisabled={isDisabled}
            isDisplayMode={isDisplayMode}
            setSelectedProduct={setSelectedProduct}
            activateCouponOnAddToBasket={activateCouponOnAddToBasket}
            productsInformation={productsInformation}
          />
        </InnerContent>
      </Content>

      <FloatingBadgesWrapper
        onClick={onCouponClick}
        $addHotDealGradient={Boolean(
          expiryDate && !hideCountdown && !disableUrgencyTriggers && label,
        )}
        $isDisplayMode={isDisplayMode}
        $isDisabled={isDisabled}
        $addUrgencyGradient={isUrgencyCard}
      >
        {expiryDate && !hideCountdown && !disableUrgencyTriggers && (
          <StyledCouponExpiryDateCountdown
            expiryDate={expiryDate}
            iconSize="XS"
            $isHotDeal={isHotDeal}
            $isUrgencyCard={isUrgencyCard}
          />
        )}
        {!disableUrgencyTriggers && (
          <HotDealBadge
            data-testid={dataTestId}
            label={label}
            isUrgencyCard={isUrgencyCard}
          />
        )}
        {isOnlyAppCoupon && !isHotDeal && (
          <CouponAppOnlyBadge data-testid={dataTestId} />
        )}
      </FloatingBadgesWrapper>
    </Wrapper>
  );
};

const getCouponTheme = ({
  theme,
  selectedTheme,
  isActive,
  isHotDeal,
}: {
  theme?: Coupon_SimpleCoupon_CouponTheme;
  selectedTheme?: Coupon_SimpleCoupon_CouponTheme;
  isActive?: boolean;
  isHotDeal?: boolean;
}) => {
  if (isHotDeal) {
    return isActive ? defaultCouponTheme.active : defaultCouponTheme.hotDeal;
  }

  if (theme && selectedTheme) {
    return isActive ? selectedTheme : theme;
  }

  return isActive ? defaultCouponTheme.active : defaultCouponTheme.inactive;
};

const InnerContent = styled.div<{
  $isHotDeal?: boolean;
}>`
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const FloatingBadgesWrapper = styled.div<{
  $addHotDealGradient?: boolean;
  $isDisplayMode?: boolean;
  $isDisabled?: boolean;
  $addUrgencyGradient?: boolean;
}>`
  display: flex;
  gap: 4px;
  left: 16px;
  position: absolute;
  top: 0;
  z-index: 2;

  ${({$addHotDealGradient}) =>
    $addHotDealGradient &&
    css`
      border-radius: 26px;
      padding: 2px;
      background: linear-gradient(
        90deg,
        #e52317 38%,
        #e5177a 63%,
        ${COLORS.PrimaryCoupon} 95%
      );
    `}

  ${({$addUrgencyGradient}) =>
    $addUrgencyGradient &&
    css`
      border-radius: 26px;
      padding: 2px;
      background: linear-gradient(90deg, #1f4bff 59.89%, #e52121 78.52%),
        ${COLORS.PrimaryCoupon};
    `}

  ${({$addHotDealGradient, $isDisplayMode, $isDisabled}) =>
    $addHotDealGradient &&
    $isDisplayMode &&
    !$isDisabled &&
    css`
      box-shadow: 0px -1px 2px -1px #e5177a,
        0px -1px 13px -1px rgba(229, 23, 122, 0.25);
    `}
`;

const InfoIcon = styled(UnstyledButton)`
  position: absolute;
  top: 21px;
  right: 10px;
  overflow: hidden;
  cursor: pointer;
  z-index: 1;

  ::after {
    border-radius: 50%;
    content: '';
    position: absolute;
    inset: 1px;
    z-index: 0;
    background-color: ${COLORS.PrimaryCoupon};
  }
`;

const InfoSVG = styled(Icon)`
  z-index: 1;
  position: relative;
`;

const StyledCouponFooter = styled(CouponFooter)<{
  $removeBorderTop?: boolean;
}>`
  border-top: 1px solid ${COLORS.BorderColorCoupon};
  ${({$removeBorderTop}) =>
    $removeBorderTop &&
    css`
      border-top: 0px;
    `}

  ${({isDisabled}) =>
    isDisabled &&
    css`
      border-top-color: ${COLORS.Grey2};
    `}
`;

const Content = styled.div<{
  $isOnlyAppCoupon: boolean;
  $isDisabled?: boolean;
  $isDisplayMode?: boolean;
  $isHotDeal?: boolean;
}>`
  border-radius: 16px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.08);
  cursor: ${({$isOnlyAppCoupon, $isDisplayMode}) =>
    !$isOnlyAppCoupon || $isDisplayMode ? 'pointer' : 'default'};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: 311px;
  z-index: 1;

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      pointer-events: none;
    `}

  ${({$isDisplayMode, $isHotDeal, $isDisabled}) =>
    $isDisplayMode &&
    $isHotDeal &&
    !$isDisabled &&
    css`
      box-shadow: 0px 0px 2px 0px #e5177a,
        0px 0px 13px 0px rgba(229, 23, 122, 0.25);
    `}
`;

const StyledCouponExpiryDateCountdown = styled(CouponExpiryDateCountdown)<{
  $isHotDeal?: boolean;
  $isUrgencyCard?: boolean;
}>`
  &&&& {
    padding: 3px 8px 3px 5px;

    ${StyledCouponExpiryDateCountdownUnitComponent} {
      line-height: 1.16;
      font-size: ${FONT_SIZES.px12};
      font-weight: ${FONT_WEIGHTS.mediumHeavy};
      color: ${COLORS.PrimaryCoupon};
      width: auto;
    }

    ${({$isHotDeal, $isUrgencyCard}) =>
      ($isHotDeal || $isUrgencyCard) &&
      css`
        border: none;
      `}
  }
`;

const Wrapper = styled.div<{
  $isHotDeal?: boolean;
  $isDisplayMode?: boolean;
  $isDisabled?: boolean;
}>`
  position: relative;
  border: 1px solid transparent;
  border-radius: 16px;
  z-index: 1;
  padding-top: 13px;

  ${({$isHotDeal, $isDisplayMode}) =>
    $isHotDeal
      ? css`
          background-clip: padding-box;

          ::before {
            content: '';
            position: absolute;
            inset: 13px 0 0 0;
            z-index: -1;
            margin: -1px;
            border-radius: 17px;
            background: ${HOT_DEALS_COUPON_GRADIENT};
            transition: background ${CARD_ANIMATION_TIME}ms ease-in;
          }

          ${$isDisplayMode &&
          css`
            ::before {
              margin: -2px;
              border-radius: 18px;
            }
          `}
        `
      : css`
          ${Content} {
            border: 1px solid ${COLORS.BorderColorCoupon};
            transition: border-color ${CARD_ANIMATION_TIME}ms ease-in;
          }
        `};

  &[data-is-active='true'] {
    ${({$isHotDeal}) =>
      $isHotDeal
        ? css`
            ::before {
              background: ${COLORS.PrimaryCoupon};
            }
          `
        : css`
            ${Content} {
              border-color: ${COLORS.PrimaryCoupon};
            }
          `}

    ${StyledCouponFooter} {
      border-top-color: ${COLORS.PrimaryCoupon};
    }

    ${FloatingBadge} {
      ${({$isHotDeal}) =>
        !$isHotDeal &&
        css`
          ::before {
            background: ${COLORS.PrimaryCoupon};
          }
        `}
    }

    ${StyledCouponTiers} {
      color: #687ee0;
    }

    ${TierShape} {
      background-color: #687ee0;
    }

    && ${StyledCouponTiers} [data-is-active='true'] {
      color: ${COLORS.White};
    }

    && [data-is-active='true'] ${TierShape} {
      background-color: ${COLORS.White};
    }

    ${BannerText} {
      background-color: ${COLORS.TertiaryCoupon};
    }
  }

  ${({$isDisplayMode}) =>
    $isDisplayMode &&
    css`
      ${Content} {
        width: 100%;
      }
      ${InfoIcon}::after {
        background-color: ${COLORS.White};
      }

      ${InfoSVG} {
        fill: ${COLORS.PrimaryCoupon};
      }
    `}

  ${({$isDisabled, $isHotDeal}) =>
    $isDisabled &&
    css`
      ${Content} {
        border-color: ${COLORS.Grey2};
      }

      ${InfoIcon}::after {
        background-color: ${COLORS.White};
      }

      ${InfoSVG} {
        fill: ${COLORS.Grey1};
      }

      ${StyledCouponExpiryDateCountdown} {
        border-color: ${COLORS.Grey2};

        ${StyledCouponExpiryDateCountdownUnitComponent}, svg {
          color: ${COLORS.Grey1};
          fill: ${COLORS.Grey1};
        }
      }

      ${FloatingBadge} {
        background-color: ${COLORS.Grey4};
        color: ${COLORS.Grey1};

        &::before {
          background: ${COLORS.Grey2};
        }
      }

      &::before {
        background: ${COLORS.Grey2};
      }

      ${$isHotDeal &&
      css`
        ${FloatingBadgesWrapper} {
          background: ${COLORS.Grey2};
        }
      `}

      ${StyledCouponExpiryDateCountdown} {
        border-color: ${COLORS.Grey1};
      }
    `}
`;
